import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface ProductFamilyMember {
  id: string;
  product_name: string;
  barcode: string;
  retail_price: number | null;
  size_variant: string | null;
  family_sort_order: number | null;
  is_parent: boolean;
  parent_id: string | null;
}

export interface PriceImpact {
  affected_product_id: string;
  affected_product_name: string;
  current_price: number | null;
  suggested_price: number | null;
  price_ratio: number | null;
}

export const useProductFamily = (productId: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch product family members
  const { data: familyMembers = [], isLoading, error } = useQuery({
    queryKey: ['product-family', productId],
    queryFn: async () => {
      console.log('🔍 Fetching family for productId:', productId);
      
      const { data, error } = await supabase.rpc('get_product_family', {
        product_id_param: productId
      });

      if (error) {
        console.error('❌ Database error in get_product_family:', error);
        throw error;
      }
      
      console.log('✅ Family members fetched:', data);
      return data as ProductFamilyMember[];
    },
    enabled: !!productId
  });

  // Add product to family
  const addToFamilyMutation = useMutation({
    mutationFn: async ({ parentId, childData }: { parentId: string; childData: any }) => {
      console.log('🔍 Adding product to family:', { parentId, childData });
      
      // Helper function to get UUID from product identifier (UUID or serial)
      const getProductUUID = async (identifier: string): Promise<string> => {
        // Check if it's already a UUID format
        if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(identifier)) {
          return identifier;
        }
        
        // Look up by serial number
        const { data, error } = await supabase
          .from('master_products')
          .select('id')
          .eq('product_serial_number', identifier)
          .eq('is_active', true)
          .single();
        
        if (error || !data) {
          throw new Error(`Product not found with identifier: ${identifier}`);
        }
        
        return data.id;
      };
      
      // Ensure parentId is a UUID
      const parentUUID = await getProductUUID(parentId);
      console.log('🔍 Resolved parent UUID:', parentUUID);
      
      const { data, error } = await supabase.rpc('add_product_to_family', {
        parent_id_param: parentUUID,
        child_product_data: childData
      });

      if (error) {
        console.error('❌ Database error:', error);
        throw error;
      }
      
      console.log('✅ Successfully added to family:', data);
      return data;
    },
    onSuccess: () => {
      toast({
        title: "Family Product Added",
        description: "Product has been added to the family successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ['product-family'] });
      queryClient.invalidateQueries({ queryKey: ['master-products'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error Adding Family Product",
        description: error.message || "Failed to add product to family",
        variant: "destructive"
      });
    }
  });

  // Get price impact analysis
  const getPriceImpact = async (newPrice: number): Promise<PriceImpact[]> => {
    const { data, error } = await supabase.rpc('get_family_price_impact', {
      product_id_param: productId,
      new_price_param: newPrice
    });

    if (error) throw error;
    
    // Transform the data to match expected interface
    return (data || []).map(item => ({
      affected_product_id: item.affected_product_id,
      current_price: item.current_price,
      suggested_price: item.suggested_price,
      affected_product_name: '', // Will need to fetch separately if needed
      price_ratio: item.current_price > 0 ? item.suggested_price / item.current_price : 1
    }));
  };

  // Remove product from family
  const removeFromFamilyMutation = useMutation({
    mutationFn: async (childProductId: string) => {
      const { error } = await supabase
        .from('master_products')
        .update({
          parent_product_id: null,
          is_parent_product: false,
          product_family_name: null,
          size_variant: null,
          family_sort_order: null
        })
        .eq('id', childProductId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Product Removed",
        description: "Product has been removed from the family.",
      });
      queryClient.invalidateQueries({ queryKey: ['product-family'] });
      queryClient.invalidateQueries({ queryKey: ['master-products'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error Removing Product",
        description: error.message || "Failed to remove product from family",
        variant: "destructive"
      });
    }
  });

  return {
    familyMembers,
    isLoading,
    error,
    addToFamily: addToFamilyMutation.mutate,
    removeFromFamily: removeFromFamilyMutation.mutate,
    getPriceImpact,
    isAddingToFamily: addToFamilyMutation.isPending,
    isRemovingFromFamily: removeFromFamilyMutation.isPending
  };
};